import { Button, Card, Modal, Typography, Grid, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';

import Icon from '../../../components/ui-components/Icon';
import { SELF_REGISTER } from '../../../shared/Routes';
import { useBatchId } from '../../../hooks/useBatchId';
import { useSelfRegistration } from './useSelfRegistration';
import WhiteRightArrow from '../../../assets/images/arrow-right.svg';
import NewPreceptorSupportIcon from '../../../assets/images/nps-button-icon.svg';
import PreworkIcon from '../../../assets/images/prework-button-icon.svg';
import { RightOutlined } from '@ant-design/icons';
import LogoutImage from '../../../assets/images/logout-red-icon.svg';
import { useAuth } from '../../../hooks/useAuth';
import { useRedirectToLogin } from '../../../hooks/useRedirectToLogin';
import { getLoggedInUserRoles } from '../../../service/RoleBasedService';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { displayError } from '../../../shared/Utils';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const preWorkRoleItems = [
	{
		title: 'regularityInPractice',
		text: 'inspirePracticeRegularity',
	},
	{
		title: 'individualSittings',
		text: 'weeklyTwoSittingsWithApplicant',
	},
	{
		title: 'heartfulCommunicationCertification',
		text: 'ensureApplicantProgramStart',
	},
	{
		title: 'literatureOrientation',
		text: 'promoteReadingHeartfulnessWay',
	},
	{
		title: 'hfnSessions',
		text: 'encourageConnectSessionParticipation',
	},
	{
		title: 'inspiringApplicants',
		text: 'awakenAspirationToServe',
	},
];

const ProposerRoleDescription = ({ batchName }: { batchName?: string }) => {
	const { t } = useTranslation();
	return (
		<>
			<p>{t('dearPreceptor')}</p>
			<p>{t('weHopeYouAreDoingWell')}</p>
			<p>
				{t('volunteeringAsAProposerForSNPInterviews', {
					batchName,
				})}
			</p>
			<p>
				{t('invitationToServeAsProposer', {
					batchName,
				})}
				&nbsp;
				{t('ifYouAreWillingToVolunteerPleaseRegister')}
			</p>
			<p>
				{t('youWillBeAssignedAMinimumOf2Applications')}&nbsp;
				{t('proposerInterviewResponsibilities')}&nbsp;
				{t('recordObservationsAndRecommend')}
			</p>
			<p>
				{t('videoConnectivityAndTimePerCandidate')}&nbsp;
				{t('importantPreparatoryStep')}&nbsp;
				{t('volunteerIfAbleAndWilling')}
			</p>
			<p>{t('weTrulyAppreciateYourTimeAndCommitment')}</p>
			<p>{t('warmRegards')}</p>
			<p className="font-bold">{t('snpTeam')}</p>
		</>
	);
};

const PreWorkRoleDescription = () => {
	const { t } = useTranslation();
	return (
		<>
			<p>{t('dearPreceptor')}</p>
			<p>
				{t('inviteToPreWorkMentor')}
				&nbsp;
				{t('ifYouAreWillingToVolunteerPleaseRegister')}&nbsp;
				{t('youWillBeAssignedAMaximumOf2Applications')}
			</p>
			<p>{t('reviewPreWorkMentorRole')}</p>
			<p>
				<strong>{t('roleOfAPreWorkMentor')}</strong>
			</p>
			{t('preWorkPhaseGuidanceNote')}
			&nbsp;
			{t('preWorkPhasePurpose')}
			<p>
				{t('youWillBeAssigned2ApplicantsAtMost')}&nbsp;
				{t('mentorSupportForAbhyasiJourney')}
			</p>
			<ul>
				{preWorkRoleItems.map(({ title, text }) => (
					<li key={title}>
						<p>
							<strong>{t(title)}-&nbsp;</strong>
							{t(text)}
						</p>
					</li>
				))}
			</ul>
			<p>
				{t('timeCommitmentPerCandidatePerWeek')}&nbsp;
				{t('signUpOnlyIfTimeAndInterest')}
			</p>
			<p>
				{t('uniqueOpportunityToMentorTwoAbhyasi')}&nbsp;
				{t('enjoyAndSupportSnpProgram')}
			</p>
			<p>{t('warmRegards')}</p>
			<p className="font-bold">{t('snpTeam')}</p>
		</>
	);
};

export const SelfRegistrationRole = () => {
	const { t } = useTranslation();
	const { navigateToRoute } = useBatchId();
	const { logout, srcmId } = useAuth();
	const { loading } = useRedirectToLogin();
	const screens = useBreakpoint();
	const isDesktop = screens.lg;
	const { batchDetails } = useSelfRegistration();

	const batchName = batchDetails?.name;
	const [modalTitle, setModalTitle] = useState<string>('');
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modalContent, setModalContent] = useState<React.ReactNode>(null);
	const [existingRoles, setExistingRoles] = useState<string[]>([]);
	const { showToastMessage } = useToastMessage();

	useEffect(() => {
		const fetchRoles = async () => {
			try {
				if (!srcmId) return;

				const userRoles = await getLoggedInUserRoles({
					loggedInUserSrcmId: srcmId,
				});

				if (!userRoles?.rolesDetails) return;

				const roles = userRoles?.rolesDetails?.map((r) => r.role.code) ?? [];

				setExistingRoles(roles);

				localStorage.setItem(
					'LOGGED_IN_USER_ROLES',
					JSON.stringify(userRoles.rolesDetails)
				);
			} catch (err) {
				const errorMsg = displayError(err);
				showToastMessage('error', errorMsg);
			}
		};

		void fetchRoles();
	}, [srcmId, showToastMessage]);

	const handleSelfRegister = useCallback(
		(role: string) => {
			localStorage.setItem('registerRole', role);
			navigateToRoute(SELF_REGISTER);
		},
		[navigateToRoute]
	);

	const openModal = useCallback(
		(role: string, content: React.ReactNode, title: string) => {
			setModalContent(
				<>
					<Text className="text-sm text-secondary-gray font-poppins font-medium">
						{content}
					</Text>
					<div className="flex mt-3 justify-center gap-4">
						<Button
							className="font-bold px-8 md:px-10"
							onClick={() => setIsModalVisible(false)}
						>
							{t('cancel')}
						</Button>
						<Button
							type="primary"
							icon={<Icon src={WhiteRightArrow} width={18} height={18} />}
							iconPosition="end"
							className="font-bold px-6 md:px-8"
							onClick={() => handleSelfRegister(role)}
						>
							{t('register')}
						</Button>
					</div>
				</>
			);
			setModalTitle(title);
			setIsModalVisible(true);
		},
		[handleSelfRegister, t]
	);

	const handleProposerRole = useCallback(() => {
		openModal(
			'proposer',
			<ProposerRoleDescription batchName={batchName} />,
			t('inviteSupportForSnpCandidates', { batchName })
		);
	}, [openModal, t, batchName]);

	const handlePreWorkRole = useCallback(() => {
		openModal(
			'preworkMentor',
			<PreWorkRoleDescription />,
			t('preWorkMentorVolunteerInvite', { batchName })
		);
	}, [openModal, t, batchName]);

	const roles = [
		{
			roleCode: UserRole.PROPOSER,
			label: t('proposer'),
			icon: NewPreceptorSupportIcon,
			onClick: handleProposerRole,
		},
		{
			roleCode: UserRole.PREWORK_MENTOR,
			label: t('preworkMentor'),
			icon: PreworkIcon,
			onClick: handlePreWorkRole,
		},
	];

	const handleLogoutClick = useCallback(() => {
		if (logout) {
			void logout();
		}
	}, [logout]);

	if (loading) return null;

	return (
		<>
			<Card className="w-full max-w-2xl mx-auto px-4 py-4 mt-16">
				<div className="flex flex-col items-center w-full">
					<div className="text-center">
						<h1 className="my-1">{t('welcome')}</h1>
						<p className="text-lg text-gray-900 font-medium my-5 text-center">
							{t('pleaseSelectARole')}
						</p>
					</div>

					{/* Buttons for each role */}
					<div className="flex flex-col gap-4 w-full max-w-xl">
						{roles.map(({ roleCode, label, icon, onClick }) => {
							const isDisabled = existingRoles.includes(roleCode);
							return (
								<Button
									key={label}
									className={`flex items-center justify-between w-full border px-4 py-8 ${
										isDisabled
											? 'bg-gray-100 border-gray-200 cursor-not-allowed'
											: 'border-gray-300'
									}`}
									onClick={onClick}
									disabled={isDisabled}
								>
									<div className="flex items-center gap-3">
										<Icon
											src={icon}
											className={`w-5 h-5 ${isDisabled ? 'opacity-40' : ''}`}
										/>
										<span
											className={`font-medium ${
												isDisabled ? 'text-gray-400' : 'text-gray-900'
											}`}
										>
											{label}
										</span>
									</div>
									<span
										className={`text-lg ${
											isDisabled ? 'text-gray-400' : 'text-primary-blue'
										}`}
									>
										<RightOutlined />
									</span>
								</Button>
							);
						})}
					</div>

					{/* Logout inside card */}
					<div className="mt-6 text-center w-full">
						<Button
							icon={
								<Icon src={LogoutImage} className="w-4 h-4 flex items-center" />
							}
							type="text"
							danger
							className="font-sans font-semibold text-base h-[50px] hover:py-4 text-red-600 hover:text-red-700"
							onClick={handleLogoutClick}
						>
							{t('logout')}
						</Button>
					</div>
				</div>
			</Card>

			<Modal
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				footer={null}
				closeIcon={false}
				width={isDesktop ? 'calc(100% - 20rem)' : undefined}
				title={<div className="text-center w-full">{modalTitle}</div>}
			>
				<Divider />
				{modalContent}
			</Modal>
		</>
	);
};
